import { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { NotificationsContext } from '@/context/NotificationsContext';
import { ApiError } from '../helpers/baseApi';
import { nanoid } from '@forma/forma-ui-kit';

interface UseQueryOptions {
  skip?: boolean,
  pollingInterval?: number,
}

const useQuery = <T>(query: (...args: any[]) => Promise<T>, options?: UseQueryOptions) => {
  const { skip = false, pollingInterval } = options || {};
  const [data, setData] = useState<T>();
  const [error, setError] = useState<ApiError>();
  const [isLoading, setIsLoading] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const response = await query();
      setData(response);
    } catch (error) {
      setError(error as ApiError);
    } finally {
      setIsLoading(false);
    }
  }, [query, pollingInterval]);

  useEffect(() => {
    if (skip || error) return;

    setIsLoading(true);
    fetchData();

    if (pollingInterval) {
      timerRef.current = setInterval(fetchData, pollingInterval);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [fetchData, error, skip]);

  const refetch = useCallback(() => {
    if (skip) return;
    // setData(undefined);
    setError(undefined);
    fetchData();
  }, [fetchData, skip]);

  return { data, error, isLoading, refetch };
};

const useMutation = <T>(mutation: (...args: any[]) => Promise<T>) => {
  const { dispatch: dispatchNotification } = useContext(NotificationsContext);

  const [data, setData] = useState<T>();
  const [error, setError] = useState<ApiError>();
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = useCallback(async (...args: any[]) => {
    setIsLoading(true);

    try {
      const response = await mutation(...args);
      setData(response);
      return response;
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
        dispatchNotification({ type: 'ADD', payload: { id: nanoid(8), type: 'ERROR', content: error.message } });
      }
      setError(error as ApiError);
      throw error as ApiError;
    } finally {
      setIsLoading(false);
    }
  }, [mutation]);

  const reset = useCallback(() => {
    setData(undefined);
    setError(undefined);
    setIsLoading(false);
  }, []);

  return { data, error, isError: !!error, isSuccess: !!data, isLoading, mutate: fetchData, reset };
};

export { useQuery, useMutation, useMutation as useLazyQuery };
